import { type ComponentType, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type AlertTone = "error" | "warn" | "info";

// `info` is the neutral note (a value set outside the app, a hint); `warn` is the
// amber "saved but not yet enforced" / "check this" note; `error` is a failed
// action. Borders and fills come from the theme tokens, so all three invert.
const TONES: Record<AlertTone, string> = {
  error: "border-red-border bg-red-bg text-red-ink",
  warn: "border-amber/35 bg-amber-bg/35 text-amber-ink",
  info: "border-edge-2 bg-base text-muted",
};

export interface AlertProps {
  tone?: AlertTone;
  // Politeness for the cases where the message appears without the user having
  // just acted (a lockout counting down); errors are assertive by default.
  "aria-live"?: "polite" | "assertive" | "off";
  // "md" is the form/panel default (a failed save under a modal's fields); "sm" is
  // the quieter footnote under a settings card, where the note is not the point of
  // the screen.
  size?: "sm" | "md";
  // An icon reads as decoration on an error the user just caused, and as
  // explanation on a standing note - so it is opt-in rather than per-tone.
  icon?: ComponentType<{ size?: number; className?: string }>;
  className?: string;
  children: ReactNode;
}

// Alert is the inline notice a form, panel or card ends with: a tinted bordered
// block of a line or two, in place of the four utility classes this used to be
// written as in sixty-odd places.
//
// NOT for transient feedback - a message that should disappear on its own is a
// Toast (lib/toast.ts). NOT for a screen that has nothing to show: a page that
// could not load its data wants `EmptyState`, or `PermissionGate` when the reason
// is a missing capability.
export function Alert({
  tone = "error", size = "md", icon: Icon, className, children, "aria-live": ariaLive,
}: AlertProps) {
  return (
    <div
      role={tone === "error" ? "alert" : undefined}
      aria-live={ariaLive}
      className={cn(
        "flex items-start gap-2 rounded-xl border",
        size === "sm" ? "px-3.5 py-2.5 text-xs leading-snug" : "px-4 py-3 text-sm font-semibold",
        TONES[tone],
        className,
      )}
    >
      {Icon && <Icon size={14} className="mt-px shrink-0" />}
      <span className="min-w-0">{children}</span>
    </div>
  );
}
